import React, { useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Form, Button, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock, faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import MainLayout from '../components/layout/MainLayout';

const ResetPasswordPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [resetError, setResetError] = useState('');
  
  // Validation schema
  const validationSchema = Yup.object({
    password: Yup.string()
      .required('Password is required')
      .min(8, 'Password must be at least 8 characters')
      .matches(/[A-Z]/, 'Password must contain at least one uppercase letter')
      .matches(/[0-9]/, 'Password must contain at least one number'),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref('password')], 'Passwords must match')
      .required('Please confirm your password')
  });
  
  // Form handling
  const formik = useFormik({
    initialValues: {
      password: '',
      confirmPassword: ''
    },
    validationSchema,
    onSubmit: async (values) => {
      setIsSubmitting(true);
      setResetError('');
      
      // In a real implementation, this would call an API
      // For now, we'll just simulate a successful reset
      setTimeout(() => {
        setResetSuccess(true);
        setIsSubmitting(false);
        
        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      }, 1500);
    }
  });
  
  // Toggle password visibility
  const [showPassword, setShowPassword] = useState(false);
  
  const togglePasswordVisibility = () => {
    setShowPassword(prevState => !prevState);
  };
  
  // Check if token is valid (in a real implementation)
  if (!token) {
    return (
      <MainLayout>
        <Container className="py-5">
          <Row className="justify-content-center">
            <Col lg={6} md={8}>
              <Card className="border-0 shadow-sm">
                <Card.Body className="p-lg-5 p-4">
                  <Alert variant="danger">
                    <h5 className="alert-heading">Invalid Reset Link</h5>
                    <p>
                      The password reset link is invalid or has expired. Please request a new password reset link.
                    </p>
                    <Link to="/forgot-password" className="btn btn-primary mt-3">
                      Request New Reset Link
                    </Link>
                  </Alert>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </MainLayout>
    );
  }
  
  return (
    <MainLayout>
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col lg={6} md={8}>
            <Card className="border-0 shadow-sm">
              <Card.Body className="p-lg-5 p-4">
                <div className="text-center mb-4">
                  <FontAwesomeIcon icon={faLock} size="3x" className="text-primary mb-3" />
                  <h2 className="fw-bold">Reset Your Password</h2>
                  <p className="text-muted">Create a new password for your account</p>
                </div>
                
                {resetSuccess ? (
                  <Alert variant="success">
                    <div className="text-center">
                      <FontAwesomeIcon icon={faCheckCircle} size="3x" className="text-success mb-3" />
                      <h5 className="alert-heading">Password Reset Successful!</h5>
                      <p>
                        Your password has been successfully reset. You will be redirected to the login page in a few seconds.
                      </p>
                      <Link to="/login" className="btn btn-success mt-3">
                        Go to Login
                      </Link>
                    </div>
                  </Alert>
                ) : (
                  <Form onSubmit={formik.handleSubmit}>
                    {resetError && (
                      <Alert variant="danger" className="mb-4">
                        {resetError}
                      </Alert>
                    )}
                    
                    <Form.Group className="mb-3">
                      <Form.Label>New Password</Form.Label>
                      <div className="input-group">
                        <span className="input-group-text bg-light">
                          <FontAwesomeIcon icon={faLock} className="text-muted" />
                        </span>
                        <Form.Control
                          type={showPassword ? "text" : "password"}
                          placeholder="Create a new password"
                          name="password"
                          value={formik.values.password}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          isInvalid={formik.touched.password && !!formik.errors.password}
                        />
                        <Button 
                          variant="light" 
                          onClick={togglePasswordVisibility}
                          className="border"
                        >
                          <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                        </Button>
                        <Form.Control.Feedback type="invalid">
                          {formik.errors.password}
                        </Form.Control.Feedback>
                      </div>
                      <Form.Text className="text-muted">
                        Password must be at least 8 characters with a number and uppercase letter.
                      </Form.Text>
                    </Form.Group>
                    
                    <Form.Group className="mb-4">
                      <Form.Label>Confirm Password</Form.Label>
                      <div className="input-group">
                        <span className="input-group-text bg-light">
                          <FontAwesomeIcon icon={faLock} className="text-muted" />
                        </span>
                        <Form.Control
                          type={showPassword ? "text" : "password"}
                          placeholder="Confirm your new password"
                          name="confirmPassword"
                          value={formik.values.confirmPassword}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          isInvalid={formik.touched.confirmPassword && !!formik.errors.confirmPassword}
                        />
                        <Form.Control.Feedback type="invalid">
                          {formik.errors.confirmPassword}
                        </Form.Control.Feedback>
                      </div>
                    </Form.Group>
                    
                    <Button 
                      variant="primary" 
                      type="submit" 
                      className="w-100 py-2" 
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Resetting Password...' : 'Reset Password'}
                    </Button>
                    
                    <div className="text-center mt-4">
                      <p className="mb-0">
                        Remember your password? <Link to="/login" className="text-primary">Sign in</Link>
                      </p>
                    </div>
                  </Form>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </MainLayout>
  );
};

export default ResetPasswordPage;
